// The opener handshake between the chat and its sign-in popup. The popup's first page is do-chat's
// own `/auth/popup`, reached before any identity-provider page (whose COOP header would cut
// `window.opener`), so it can still talk to the chat that opened it.

export const AUTH_READY_MESSAGE = "dochat-auth-ready";
export const AUTH_SECRET_MESSAGE = "dochat-auth-secret";

// How long the popup waits for the chat's answer before it gives up.
export const HANDSHAKE_TIMEOUT_MS = 10_000;

type ReadyMessage = { type: typeof AUTH_READY_MESSAGE; txId: string };
type SecretMessage = { type: typeof AUTH_SECRET_MESSAGE; txId: string; txSecret: string };

const isReadyMessage = (data: unknown, txId: string): data is ReadyMessage =>
  !!data &&
  typeof data === "object" &&
  (data as ReadyMessage).type === AUTH_READY_MESSAGE &&
  (data as ReadyMessage).txId === txId;

const isSecretMessage = (data: unknown, txId: string): data is SecretMessage =>
  !!data &&
  typeof data === "object" &&
  (data as SecretMessage).type === AUTH_SECRET_MESSAGE &&
  (data as SecretMessage).txId === txId &&
  typeof (data as SecretMessage).txSecret === "string";

/**
 * Chat side. Opens the popup — synchronously, so call it from the click handler — and hands it
 * the secret once it announces itself. Only the popup it opened, on the auth host, gets it: a
 * window opened by anyone else, or any other origin, is ignored.
 */
export function openAuthPopup({
  authUrl,
  txId,
  txSecret,
  win = window,
}: {
  authUrl: string;
  txId: string;
  txSecret: string;
  win?: Window;
}): { popup: Window | null; dispose: () => void } {
  const authOrigin = new URL(authUrl).origin;
  const popup = win.open(
    `${authOrigin}/auth/popup?tx=${encodeURIComponent(txId)}`,
    "dochat-auth",
    "popup,width=480,height=680"
  );

  const onMessage = (event: MessageEvent) => {
    if (!popup || event.source !== popup || event.origin !== authOrigin) return;
    if (!isReadyMessage(event.data, txId)) return;
    const answer: SecretMessage = { type: AUTH_SECRET_MESSAGE, txId, txSecret };
    popup.postMessage(answer, authOrigin);
    dispose();
  };
  const dispose = () => win.removeEventListener("message", onMessage);

  if (popup) win.addEventListener("message", onMessage);
  return { popup, dispose };
}

/**
 * Popup side. Announces itself to its opener — exactly at the chat's origin — and resolves with
 * the secret, accepted only from that origin. Resolves null when there is no opener (a link sent
 * by someone else) or when the chat does not answer in time: the sign-in must then not start.
 */
export function receiveTxSecret({
  txId,
  origin,
  win = window,
  timeoutMs = HANDSHAKE_TIMEOUT_MS,
}: {
  txId: string;
  origin: string;
  win?: Window;
  timeoutMs?: number;
}): Promise<string | null> {
  const opener = win.opener as Window | null;
  if (!opener) return Promise.resolve(null);

  return new Promise((resolve) => {
    const finish = (secret: string | null) => {
      win.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve(secret);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== opener) return;
      if (isSecretMessage(event.data, txId)) finish(event.data.txSecret);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    win.addEventListener("message", onMessage);
    const ready: ReadyMessage = { type: AUTH_READY_MESSAGE, txId };
    opener.postMessage(ready, origin);
  });
}
